<template>
  <Layout>
    <div class="py-8">
      <!-- Header -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div class="flex justify-between items-center">
          <div class="flex items-center">
            <h1 class="text-3xl font-bold text-gray-900">
              {{ $t('admin.dashboard.title') }}
            </h1>
          </div>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-600">
              {{ $t('admin.dashboard.connectedAs') }} {{ user?.email }}
            </span>
          </div>
        </div>
      </div>

      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <!-- Stats Cards -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <svg class="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-500">Total fichiers</p>
                <p class="text-2xl font-semibold text-gray-900">{{ stats.totalFiles }}</p>
              </div>
            </div>
          </div>

          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-500">Fichiers actifs</p>
                <p class="text-2xl font-semibold text-gray-900">{{ stats.activeFiles }}</p>
              </div>
            </div>
          </div>

          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <svg class="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-500">Fichiers expirés</p>
                <p class="text-2xl font-semibold text-gray-900">{{ stats.expiredFiles }}</p>
              </div>
            </div>
          </div>

          <div class="card p-6">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <svg class="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-500">Téléchargements</p>
                <p class="text-2xl font-semibold text-gray-900">{{ Math.round(stats.avgDownloads) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs -->
        <div class="mb-8">
          <nav class="flex space-x-8">
            <button
              @click="activeTab = 'files'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'files'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              Fichiers récents
            </button>
            <button
              @click="activeTab = 'branding'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'branding'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              Personnalisation
            </button>
            <button
              @click="activeTab = 'settings'"
              :class="[
                'py-2 px-1 border-b-2 font-medium text-sm',
                activeTab === 'settings'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
            >
              Paramètres
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <!-- Files Tab -->
        <div v-if="activeTab === 'files'" class="card p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">Fichiers récents</h3>
          
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fichier</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Taille</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Téléchargements</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expire le</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="file in recentFiles" :key="file.id">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="text-sm font-medium text-gray-900">{{ file.fileName }}</div>
                    <div class="text-sm text-gray-500">{{ file.uploadedBy }}</div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatFileSize(file.size) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ file.downloadCount }} / {{ file.maxDownloads }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {{ formatDate(file.expirationDate) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      @click="deleteFile(file.id)"
                      class="text-red-600 hover:text-red-900"
                    >
                      Supprimer
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Branding Tab -->
        <div v-if="activeTab === 'branding'" class="card p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">Personnalisation</h3>
          
          <form @submit.prevent="updateBranding" class="space-y-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Nom de l'entreprise
              </label>
              <input
                v-model="branding.name"
                type="text"
                class="input-field"
                required
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                URL du logo
              </label>
              <input
                v-model="branding.logoUrl"
                type="url"
                class="input-field"
                placeholder="https://exemple.com/logo.png"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Couleur principale
              </label>
              <input
                v-model="branding.colorTheme.primary"
                type="color"
                class="h-10 w-20 rounded border border-gray-300"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Template email personnalisé (HTML)
              </label>
              <textarea
                v-model="branding.emailTemplate"
                rows="6"
                class="input-field"
                placeholder="Template HTML pour les emails..."
              ></textarea>
            </div>

            <button
              type="submit"
              :disabled="saving"
              class="btn-primary"
            >
              {{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </form>
        </div>

        <!-- Settings Tab -->
        <div v-if="activeTab === 'settings'" class="card p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-6">Paramètres</h3>
          
          <form @submit.prevent="updateSettings" class="space-y-6">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Taille maximale de fichier (MB)
              </label>
              <input
                v-model.number="settings.uploadLimit"
                type="number"
                class="input-field"
                min="1"
                max="10000"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Durée d'expiration (jours)
              </label>
              <input
                v-model.number="settings.expirationPolicy"
                type="number"
                class="input-field"
                min="1"
                max="365"
              />
            </div>

            <button
              type="submit"
              :disabled="saving"
              class="btn-primary"
            >
              {{ saving ? 'Sauvegarde...' : 'Sauvegarder' }}
            </button>
          </form>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import axios from 'axios'
import Layout from './Layout.vue'

export default {
  name: 'AdminDashboard',
  components: {
    Layout
  },
  data() {
    return {
      user: null,
      stats: {},
      recentFiles: [],
      activeTab: 'files',
      branding: {
        name: '',
        logoUrl: '',
        colorTheme: { primary: '#3B82F6' },
        emailTemplate: ''
      },
      settings: {
        uploadLimit: 1000,
        expirationPolicy: 7
      },
      saving: false
    }
  },
  async mounted() {
    await this.checkAuth()
    await this.loadData()
  },
  methods: {
    async checkAuth() {
      const token = localStorage.getItem('token')
      const userData = localStorage.getItem('user')
      
      if (!token || !userData) {
        this.$router.push('/login')
        return
      }

      try {
        this.user = JSON.parse(userData)
        
        // Vérifier que l'utilisateur est admin
        if (this.user.role !== 'admin') {
          this.$router.push('/')
          return
        }
        
        // Set default axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      } catch (error) {
        console.error('Error parsing user data:', error)
        localStorage.removeItem('user')
        localStorage.removeItem('token')
        this.$router.push('/login')
      }
    },
    async loadData() {
      try {
        const subdomain = this.getSubdomain()
        const response = await axios.get(`/api/companies/${subdomain}/stats`)
        
        this.stats = response.data.stats
        this.recentFiles = response.data.recentFiles
        
        // Load company info for branding
        const companyResponse = await axios.get(`/api/companies/${subdomain}`)
        const company = companyResponse.data
        
        this.branding.name = company.name
        this.branding.logoUrl = company.logoUrl || ''
        this.branding.colorTheme = company.colorTheme || { primary: '#3B82F6' }
        this.branding.emailTemplate = company.emailTemplate || ''
        
        this.settings.uploadLimit = Math.round(company.uploadLimit / 1024 / 1024) // Convert to MB
        this.settings.expirationPolicy = company.expirationPolicy
        
      } catch (error) {
        console.error('Error loading data:', error)
        if (error.response?.status === 401) {
          this.logout()
        }
      }
    },
    getSubdomain() {
      const hostname = window.location.hostname
      const parts = hostname.split('.')
      return parts.length > 2 ? parts[0] : 'default'
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('fr-FR')
    },
    async updateBranding() {
      this.saving = true
      
      try {
        const subdomain = this.getSubdomain()
        await axios.put(`/api/companies/${subdomain}/branding`, this.branding)
        alert('Personnalisation mise à jour avec succès')
      } catch (error) {
        console.error('Error updating branding:', error)
        alert('Erreur lors de la mise à jour')
      } finally {
        this.saving = false
      }
    },
    async updateSettings() {
      this.saving = true
      
      try {
        const subdomain = this.getSubdomain()
        await axios.put(`/api/companies/${subdomain}/settings`, {
          uploadLimit: this.settings.uploadLimit * 1024 * 1024, // Convert to bytes
          expirationPolicy: this.settings.expirationPolicy
        })
        alert('Paramètres mis à jour avec succès')
      } catch (error) {
        console.error('Error updating settings:', error)
        alert('Erreur lors de la mise à jour')
      } finally {
        this.saving = false
      }
    },
    async deleteFile(fileId) {
      if (!confirm('Êtes-vous sûr de vouloir supprimer ce fichier ?')) {
        return
      }
      
      try {
        await axios.delete(`/api/files/${fileId}`)
        await this.loadData() // Reload data
        alert('Fichier supprimé avec succès')
      } catch (error) {
        console.error('Error deleting file:', error)
        alert('Erreur lors de la suppression')
      }
    },
    logout() {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      delete axios.defaults.headers.common['Authorization']
      this.$router.push('/')
    }
  }
}
</script> 